import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { forgotPassword, verifyOtp, resendOtp as resendOtpApi, resetPassword } from '../services/authService';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Reset
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return false;
    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    // Focus next input
    if (element.nextSibling && element.value !== "") {
      element.nextSibling.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && e.target.previousSibling) {
      e.target.previousSibling.focus();
    }
  };

  const formatTimer = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleResend = async () => {
    if (timer === 0) {
      try {
        setIsLoading(true);
        await resendOtpApi(email);
        setTimer(60);
        toast.success("Verification code resent successfully!");
      } catch (error) {
        console.log('An error occurred : ', error);
        // toast.error("Failed to resend code. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const maskEmail = (email) => {
    if (!email) return 'j***@gmail.com';
    const [user, domain] = email.split('@');
    if (!domain) return email;
    return `${user.substring(0, 1)}***@${domain}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address.");
      return;
    }
    try {
      setIsLoading(true);
      await forgotPassword(email);
      setStep(2);
      toast.success("Verification code sent to your email!");
    } catch (error) {
      console.log('An error occurred : ', error);
      // toast.error("Failed to send verification code. Please check your email.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    if (otpCode.length < 6) {
      toast.error("Please enter the full 6-digit code.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await verifyOtp(email, otpCode);
      if (response.data.success || response.status === 200 || response.data.message?.includes("verified")) {
        toast.success("Email verified successfully!");
        setStep(3);
      }
    } catch (error) {
      console.log('An error occurred : ', error);
      // toast.error("Invalid verification code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    try {
      setIsLoading(true);
      await resetPassword(email, newPassword, confirmPassword);
      toast.success("Password reset successfully! Please sign in with your new password.");
      navigate('/');
    } catch (error) {
      console.log('An error occurred : ', error);
      // toast.error("Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = (val) => ({
    width: '100%', padding: '13px 14px 13px 42px',
    background: 'rgba(255,255,255,0.06)',
    border: `1.5px solid ${val ? 'rgba(102,178,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: 12, fontSize: 14,
    color: 'white', outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'border-color 0.2s',
  });

  const buttonStyle = {
    width: '100%', padding: '14px',
    background: isLoading ? 'rgba(102,178,255,0.5)' : 'var(--blue)',
    border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700,
    color: 'var(--navy)', cursor: isLoading ? 'not-allowed' : 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'all 0.2s',
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--navy)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      {/* BACKGROUND PATTERN */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none',
      }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: 400 + i * 100, height: 400 + i * 100,
            borderRadius: '50%',
            border: '1px solid rgba(102,178,255,0.06)',
            top: '50%', left: '50%',
            transform: `translate(-50%, -50%)`,
          }} />
        ))}
        <div style={{ position: 'absolute', top: -120, right: -120, width: 400, height: 400, borderRadius: '50%', background: 'rgba(102,178,255,0.04)' }} />
        <div style={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,203,43,0.03)' }} />
      </div>

      <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>

        {/* BACK BUTTON */}
        <button 
          onClick={() => step === 1 ? navigate("/") : setStep(step - 1)}
          style={{ 
            display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', 
            color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            marginBottom: 24, padding: 0, transition: 'color 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.color = 'white'}
          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Login
        </button>

        {/* LOGO */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
           <div style={{
            width: 64, height: 64, borderRadius: 18,
            background: 'rgba(102,178,255,0.12)',
            border: '1px solid rgba(102,178,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="1.8" strokeLinecap="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 24, fontWeight: 700, color: 'white' }}>
            Account <span style={{ color: 'var(--gold)' }}>Recovery</span>
          </div>
        </div>

        {/* CARD */}
        <div style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(102,178,255,0.15)',
          borderRadius: 20, padding: '36px 32px',
          backdropFilter: 'blur(10px)',
        }}>
          
          {step === 1 && (
            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: 'white', marginBottom: 6 }}>
                  Forgot Password?
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                  Enter your admin email address to receive a 6-digit verification code.
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 8 }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.8" strokeLinecap="round" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@gateguide.io"
                    style={inputStyle(email)}
                    onFocus={e => e.target.style.borderColor = 'rgba(102,178,255,0.6)'}
                    onBlur={e => e.target.style.borderColor = email ? 'rgba(102,178,255,0.4)' : 'rgba(255,255,255,0.1)'}
                    required
                  />
                </div>
              </div>

              <button type="submit" disabled={isLoading} style={buttonStyle}>
                {isLoading ? "Sending Code..." : "Send Verification Code"}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerify}>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: 'white', marginBottom: 6 }}>
                  Verify Email
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                  We've sent a code to <span style={{ color: 'var(--blue)', fontWeight: 600 }}>{maskEmail(email)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
                {otp.map((data, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    style={{
                      width: '100%', height: 48,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1.5px solid rgba(255,255,255,0.1)',
                      borderRadius: 10, fontSize: 18, fontWeight: 700,
                      color: 'white', textAlign: 'center', outline: 'none',
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                    value={data}
                    onChange={e => handleOtpChange(e.target, index)}
                    onKeyDown={e => handleKeyDown(e, index)}
                    onFocus={e => e.target.select()}
                  />
                ))}
              </div>

              <button type="submit" disabled={isLoading} style={{ ...buttonStyle, marginBottom: 20 }}>
                {isLoading ? "Verifying..." : "Verify Code"}
              </button>

              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Didn't receive the code? </span>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={timer > 0 || isLoading}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    fontSize: 13, fontWeight: 700,
                    color: (timer > 0 || isLoading) ? 'rgba(255,255,255,0.2)' : 'var(--blue)',
                    cursor: (timer > 0 || isLoading) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Resend {timer > 0 && `(${formatTimer(timer)})`}
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: 'white', marginBottom: 6 }}>
                  Reset Password
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                  Please set a new, secure password for your admin account.
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 8 }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.8" strokeLinecap="round" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}>
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    style={{ ...inputStyle(newPassword), paddingLeft: 42, paddingRight: 46 }}
                    required
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.8" strokeLinecap="round">
                      {showPass
                        ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>
                        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                      }
                    </svg>
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 8 }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.8" strokeLinecap="round" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}>
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    style={inputStyle(confirmPassword)}
                    required
                  />
                </div>
              </div>

              <button type="submit" disabled={isLoading} style={buttonStyle}>
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}

        </div>

        {/* FOOTER */}
        {/* <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>
          GateGuide Admin · Peri Studios · 2026
        </div> */}
      </div>

      <style>{`
        input::placeholder { color: rgba(255,255,255,0.2) !important; }
      `}</style>
    </div>
  );
};

export default ForgotPassword;
